
import { useEffect } from 'react';
import { Header } from '../../../components/feature/Header';
import { Footer } from '../../../components/feature/Footer';
import { FSBOHero } from './components/FSBOHero';
import { WhatIsFSBO } from './components/WhatIsFSBO';
import { TenantPrivacyBenefits } from './components/TenantPrivacyBenefits';
import { PrivateConfidentialSale } from './components/PrivateConfidentialSale';
import { TaxStructuringBenefits } from './components/TaxStructuringBenefits';
import { CommissionSavings } from './components/CommissionSavings';
import { DirectBuyerAdvantages } from './components/DirectBuyerAdvantages';
import { FSBOProcess } from './components/FSBOProcess';
import { FSBOFAQ } from './components/FSBOFAQ';
import { FSBOFinalCTA } from './components/FSBOFinalCTA';

const ForSaleByOwnerPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Set document title and meta tags using React 19 features
    document.title = 'For Sale by Owner Apartment Building: FSBO Guide | Save Commissions & Protect Privacy';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Sell your apartment building for sale by owner and save 5-6% in commissions. Learn how to protect tenant privacy, keep the sale confidential, and structure tax-advantaged deals with direct buyers. Complete FSBO guide for apartment building owners.');
    } else {
      const newMetaDescription = document.createElement('meta');
      newMetaDescription.name = 'description';
      newMetaDescription.content = 'Sell your apartment building for sale by owner and save 5-6% in commissions. Learn how to protect tenant privacy, keep the sale confidential, and structure tax-advantaged deals with direct buyers. Complete FSBO guide for apartment building owners.';
      document.head.appendChild(newMetaDescription);
    }

    // Add structured data
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "For Sale by Owner Apartment Building: Complete FSBO Guide",
      "description": "Comprehensive guide to selling your apartment building for sale by owner. Learn how to save commissions, protect tenant privacy, maintain confidentiality, and structure tax-advantaged deals with direct buyers.",
      "image": "https://public.readdy.ai/ai/img_res/2063d8ff98ea995da0e704882630f92f.jpg",
      "author": {
        "@type": "Organization",
        "name": "Apartment Building Buyers"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Apartment Building Buyers"
      },
      "datePublished": "2024-01-15",
      "dateModified": new Date().toISOString().split('T')[0]
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Is it legal to sell my apartment building for sale by owner?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, it's completely legal to sell your apartment building without a real estate agent. You own the property and have the right to sell it however you choose. Many commercial property owners successfully sell FSBO, especially when working with experienced direct buyers who understand the process."
          }
        },
        {
          "@type": "Question",
          "name": "How much can I save by selling FSBO?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You can save 5-6% in broker commissions, which amounts to $120,000 on a $2M property, $180,000 on a $3M property, or $300,000 on a $5M property. Additionally, you save on marketing expenses, staging costs, and carrying costs during extended listing periods."
          }
        },
        {
          "@type": "Question",
          "name": "How do I protect tenant privacy when selling?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Selling to a direct buyer requires only 1-2 property showings instead of dozens. This minimizes disruption to your tenants and protects their privacy. Professional investors can evaluate properties quickly without repeatedly disturbing tenants."
          }
        },
        {
          "@type": "Question",
          "name": "Can I keep the sale confidential from my tenants?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, a direct sale can remain completely confidential until closing. Unlike traditional listings that appear on MLS and commercial websites, a private transaction with a direct buyer means tenants typically don't learn about the sale until after closing when they receive the legally required notice of ownership change."
          }
        },
        {
          "@type": "Question",
          "name": "What tax-advantaged structures are available?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Direct buyers can accommodate 1031 exchanges, installment sales, partial interest sales, and opportunity zone investments. These structures can significantly reduce your tax burden compared to traditional all-cash sales. Working with a flexible buyer allows you to implement the tax strategy your advisors recommend."
          }
        }
      ]
    };

    // Add schema scripts
    const existingSchemaScript = document.querySelector('script[data-schema="article"]');
    if (!existingSchemaScript) {
      const schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.setAttribute('data-schema', 'article');
      schemaScript.textContent = JSON.stringify(schemaData);
      document.head.appendChild(schemaScript);
    }

    const existingFaqSchemaScript = document.querySelector('script[data-schema="faq"]');
    if (!existingFaqSchemaScript) {
      const faqSchemaScript = document.createElement('script');
      faqSchemaScript.type = 'application/ld+json';
      faqSchemaScript.setAttribute('data-schema', 'faq');
      faqSchemaScript.textContent = JSON.stringify(faqSchema);
      document.head.appendChild(faqSchemaScript);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <FSBOHero />
        <WhatIsFSBO />
        <TenantPrivacyBenefits />
        <PrivateConfidentialSale />
        <TaxStructuringBenefits />
        <CommissionSavings />
        <DirectBuyerAdvantages />
        <FSBOProcess />
        <FSBOFAQ />
        <FSBOFinalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default ForSaleByOwnerPage;
